import { useCallback, useEffect, useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useSelections, useSet, useToggle } from 'ahooks'
import { CircleCheckIcon, ListCheck, TrashIcon } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip.tsx'
import { confirm } from '@/components/confirmation.tsx'
import { Empty } from '@/components/empty.tsx'
import { Breadcrumbs } from '@/components/layout/breadcrumbs.tsx'
import { PageTitle } from '@/components/layout/page-title.tsx'
import { LoadingSpinner } from '@/components/spinner/loading-spinner.tsx'
import { DocumentsList } from '@/features/documents/documents-list.tsx'
import { useDeleteDocuments } from '@/features/documents/hooks/use-delete-documents.ts'
import { useSearchDocuments } from '@/features/documents/hooks/use-search-documents.ts'

export const Route = createFileRoute('/_authenticated/documents/processing/')({
  component: ProcessingDocuments,
})

function ProcessingDocuments() {
  const [documentIdsSet, { add: addToDocumentIdsSet }] = useSet<string>()
  const documentIds = useMemo(
    () => Array.from(documentIdsSet),
    [documentIdsSet]
  )
  const findProcessingDocumentsQuery = useSearchDocuments(
    { status: { in: ['processing'] } },
    { refetchInterval: 10000, staleTime: 0, gcTime: 0 }
  )
  const processingDocuments = findProcessingDocumentsQuery?.data?.data?.data

  useEffect(() => {
    processingDocuments?.forEach((d) => addToDocumentIdsSet(d.id))
  }, [addToDocumentIdsSet, processingDocuments])

  // documents query

  const findDocumentsQuery = useSearchDocuments(
    { id: { in: documentIds } },
    { refetchInterval: 2000, enabled: documentIds.length > 0 }
  )

  const documents = findDocumentsQuery.data?.data?.data

  const isEmpty = useMemo(() => {
    return (
      !findProcessingDocumentsQuery.isFetching &&
      !findDocumentsQuery.isFetching &&
      !documents?.length
    )
  }, [
    documents?.length,
    findDocumentsQuery.isFetching,
    findProcessingDocumentsQuery.isFetching,
  ])

  const [isMultiselectToggleActive, multiselectToggleActions] = useToggle()
  const documentsSelections = useSelections(documentIds)
  useEffect(() => {
    if (!isMultiselectToggleActive) {
      documentsSelections.clearAll()
    }
  }, [documentsSelections, isMultiselectToggleActive])

  const deleteProcessingDocumentsMutation = useDeleteDocuments({
    onSuccess() {
      findProcessingDocumentsQuery.refetch()
    },
  })
  const handleMultiDeleteBtnClick = useCallback(() => {
    confirm({
      title: `Sei sicuro di voler eliminare i documenti?`,
      content: `${documentsSelections.selected.length} documenti saranno eliminati`,
      okButtonVariant: 'destructive',
      okText: 'Conferma',
      async onOk() {
        await deleteProcessingDocumentsMutation.mutateAsync({
          documentIds: documentsSelections.selected,
        })
        toast('Documenti eliminati', { icon: <CircleCheckIcon /> })
      },
    })
  }, [deleteProcessingDocumentsMutation, documentsSelections.selected])

  return (
    <ScrollArea className="flex h-full flex-col p-4">
      <Breadcrumbs
        items={[{ name: 'Documenti' }, { name: 'In elaborazione' }]}
      />
      <div className="mb-4 flex items-center justify-between">
        <PageTitle className="mb-0">Documenti in elaborazione</PageTitle>

        <div className="flex items-center gap-x-2">
          {isMultiselectToggleActive && (
            <Button
              variant="destructive"
              disabled={documentsSelections.noneSelected}
              onClick={handleMultiDeleteBtnClick}
            >
              <TrashIcon className="size-4" />
              Elimina
            </Button>
          )}
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant={isMultiselectToggleActive ? 'default' : 'outline'}
                  size="icon"
                  onClick={multiselectToggleActions.toggle}
                >
                  <ListCheck />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Selezione Multipla</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {(findProcessingDocumentsQuery.isLoading ||
        findDocumentsQuery.isLoading) && (
        <div className="max-w-96 py-10">
          <LoadingSpinner />
        </div>
      )}

      {isEmpty && (
        <div className="max-w-96 py-10">
          <Empty />
        </div>
      )}

      {findDocumentsQuery.isSuccess && documents && documents?.length > 0 && (
        <DocumentsList
          documents={documents}
          uploadDateVisible={false}
          statusVisible
          processingStatusVisible
          leftSlot={(document) =>
            isMultiselectToggleActive ? (
              <div className="flex items-center pl-2 pr-4">
                <Checkbox
                  checked={documentsSelections.isSelected(document.id)}
                />
              </div>
            ) : null
          }
          onItemClick={(e, document) => {
            if (isMultiselectToggleActive) {
              e.preventDefault()
              documentsSelections.toggle(document.id)
            }
          }}
        />
      )}
    </ScrollArea>
  )
}
